import { useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { buildIframeSnippet, buildReactSnippet } from "util/iframe-embed-code";
import { H4, P } from "visual-components/util/texts";
import { SnippetPreview } from "./SnippetPreview";

const FORMAT_HTML = "html";
const FORMAT_REACT = "react";

/**
 * Iframe-only snippet panel with width/height/border/scrolling customization.
 * Owns all customization state locally; only the resulting snippet is observable
 * (via the read-only preview and the user's clipboard).
 *
 * @param {Object} props
 * @param {string} props.campaignUid - Campaign UID used to construct the iframe src.
 * @returns {import("react").ReactElement}
 */
export function IframeSnippetPanel(props) {
  const { campaignUid } = props;

  const [width, setWidth] = useState(100);
  const [widthUnit, setWidthUnit] = useState("%");
  const [height, setHeight] = useState(800);
  const [border, setBorder] = useState(false);
  const [scrolling, setScrolling] = useState(true);
  const [format, setFormat] = useState(FORMAT_HTML);

  const snippet = useMemo(() => {
    const buildOptions = {
      campaignUid,
      width: `${width}${widthUnit}`,
      height,
      border,
      scrolling,
    };

    return format === FORMAT_REACT
      ? buildReactSnippet(buildOptions)
      : buildIframeSnippet(buildOptions);
  }, [format, campaignUid, width, widthUnit, height, border, scrolling]);

  /**
   * @param {import("react").ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleWidthChange = (event) => {
    const next = Number(event.target.value);
    setWidth(Number.isFinite(next) ? next : 0);
  };

  /**
   * @param {import("react").MouseEvent<HTMLElement>} _event
   * @param {string|null} unit - Newly selected unit, or null if the user deselected.
   * @returns {void}
   */
  const handleWidthUnitChange = (_event, unit) => {
    if (unit) setWidthUnit(unit);
  };

  /**
   * @param {import("react").ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleHeightChange = (event) => {
    const next = Number(event.target.value);
    setHeight(Number.isFinite(next) ? next : 0);
  };

  /**
   * @param {import("react").MouseEvent<HTMLElement>} _event
   * @param {string|null} next - Newly selected format, or null if deselected.
   * @returns {void}
   */
  const handleFormatChange = (_event, next) => {
    if (next) setFormat(next);
  };

  /**
   * @returns {Promise<void>}
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toastr.success("snippet copied to clipboard", null, { timeOut: 1000 });
    } catch (err) {
      toastr.error("Could not copy snippet — please copy manually.");
    }
  };

  const isReact = format === FORMAT_REACT;

  return (
    <div className="row text-left" style={{ marginTop: "16px" }}>
      <div className="col-sm-12">
        <H4 multiline>
          Paste the snippet below into any page on your website. Both variants preserve
          participating-brand tracking by forwarding the parent page's query string into the iframe.
        </H4>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2 }}>
          <ToggleButtonGroup
            value={format}
            exclusive
            size="small"
            onChange={handleFormatChange}
            aria-label="Snippet format"
          >
            <ToggleButton value={FORMAT_HTML} aria-label="HTML iframe snippet">
              HTML
            </ToggleButton>
            <ToggleButton value={FORMAT_REACT} aria-label="React component snippet">
              React
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            label="Width"
            type="number"
            size="small"
            value={width}
            onChange={handleWidthChange}
            sx={{ width: 120 }}
          />
          <ToggleButtonGroup
            value={widthUnit}
            exclusive
            size="small"
            onChange={handleWidthUnitChange}
            aria-label="Width unit"
          >
            <ToggleButton value="%" aria-label="percent">
              %
            </ToggleButton>
            <ToggleButton value="px" aria-label="pixels">
              px
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Height (px)"
            type="number"
            size="small"
            value={height}
            onChange={handleHeightChange}
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={
              <Switch checked={border} onChange={(event) => setBorder(event.target.checked)} />
            }
            label="Show border"
          />
          <FormControlLabel
            control={
              <Switch
                checked={scrolling}
                onChange={(event) => setScrolling(event.target.checked)}
              />
            }
            label="Allow scrolling"
          />
        </Box>

        <SnippetPreview
          label={isReact ? "React component" : "Iframe snippet"}
          value={snippet}
          copyAriaLabel={isReact ? "Copy React component" : "Copy iframe snippet"}
          onCopy={handleCopy}
          minRows={isReact ? 14 : 6}
        />

        <Box sx={{ marginTop: 2 }}>
          <P multiline>
            Good to know:
            <br />• No need to publish first - the embed previews in any state.
            <br />• Not showing? Your site builder may strip the <code>&lt;iframe&gt;</code>/
            <code>&lt;script&gt;</code> - use a “custom HTML / embed” block and allow cross-origin
            iframes in your CSP.
            <br />• Test the <code>src</code> URL in a browser tab: if it loads there, the issue is
            your hosting page, not DojoMojo.
          </P>
        </Box>
      </div>
    </div>
  );
}
